import React, { useState, useRef, useEffect } from 'react';

const LoanQuoteStep1 = ({
        onSubmitQuote,
        loanQuotationInput,
        onChangeLoanQuotationInput,
        isSubmitting,
        errors
    }) => {

    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [companySearchError, setCompanySearchError] = useState(null);
    const [isUserTyping, setIsUserTyping] = useState(false);

    const companyInputRef = useRef(null);

    const fetchCompanies = async (query) => {
        if (query.length < 3) {
            setCompanySuggestions([]);
            return;
        }

        setLoading(true);
        setCompanySearchError(null);

        try {
            const response = await fetch(`/wp-json/loan-quote/v1/company-search?query=${query}`);
            
            if (!response.ok) throw new Error("Failed to fetch companies");
            
            const data = await response.json();
            setCompanySuggestions(data || []);
        } catch (err) {
            setCompanySearchError("Couldn't fetch companies. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isUserTyping) return;

        const delayDebounce = setTimeout(() => {
            if (companySearchTerm) fetchCompanies(companySearchTerm);
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [companySearchTerm, isUserTyping]);

    return (
        <form onSubmit={onSubmitQuote} className="needs-validation">
            <h4>Loan quotation</h4>
            <div className="row gx-5 gy-3">
                <div className="col-md-6">
                    <label htmlFor="loanAmount" className="form-label">How much would you like to borrow?&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input
                        type="number"
                        id="loanAmount"
                        name="loanAmount"
                        className={`form-control ${errors.loanAmount ? "is-invalid" : ""}`}
                        value={loanQuotationInput.loanAmountString}
                        placeholder="The minimum we offer is £3,000"
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, loanAmountString: e.target.value})}
                    />
                    {errors.loanAmount && <div className="invalid-feedback">{errors.loanAmount}</div>}
                </div>

                <div className="col-md-6">
                    <label htmlFor="loan-term" className="form-label">Over what period (months)?&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input 
                        type="range"
                        id="loan-term"
                        name="loan-term"
                        className="form-range lqc-form-range"
                        min="0"
                        max="5"
                        step="1" 
                        value={loanQuotationInput.loanTermCode}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, loanTermCode: e.target.value})}
                    />
                    <div className="d-flex justify-content-between px-1 text-muted small">
                        <span>24</span>
                        <span>36</span>
                        <span>48</span>
                        <span>60</span>
                        <span>72</span>
                        <span>84</span>
                    </div>
                </div>

                <div className="col-md-12">
                    <fieldset className={`form-control ${errors.company ? "is-invalid" : ""}`}>
                        <legend className="float-none w-auto px-2">Limited company or sole-trader/partner name&nbsp;
                            <span aria-label="required">*</span>
                        </legend>
                        <div className="row gx-5">
                            <div className="col-md-6" style={{ position: "relative" }}>
                                <label htmlFor="company" className="form-label">Limited company name</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    ref={companyInputRef}
                                    className="form-control"
                                    value={companySearchTerm}
                                    placeholder="Start typing to find your company"
                                    onChange={(e) => {
                                        setCompanySearchTerm(e.target.value);
                                        setCompanySuggestions([]);
                                        setCompanySearchError(null);
                                        onChangeLoanQuotationInput({...loanQuotationInput, selectedCompany: ""});
                                        setIsUserTyping(true);
                                    }}
                                />
                                {companySuggestions.length > 0 && (
                                    <ul className="dropdown">
                                        {companySuggestions.map((c) => (
                                            <li
                                                key={c.id}
                                                onClick={() => {
                                                    setIsUserTyping(false);
                                                    setCompanySearchTerm(`${c.name} (${c.id})`);
                                                    onChangeLoanQuotationInput({...loanQuotationInput, selectedCompany: `${c.id}:${c.name}`});
                                                    setCompanySuggestions([]);
                                                    setTimeout(() => {
                                                        companyInputRef.current?.focus();
                                                    }, 0);
                                                }}
                                            >{c.name} - <span className="company-number">{c.id}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                
                                {loading && <p className="loading-indicator">Loading...</p>}
                                {companySearchError && <p class="error">{companySearchError}</p>}
                            </div>
                        
                            <div className="col-md-6">
                                <label htmlFor="sole-trader" className="form-label">Sole-trader/Partner name</label>
                                <input
                                    type="text"
                                    id="sole-trader"
                                    name="sole-trader"
                                    className="form-control"
                                    value={loanQuotationInput.soleTraderInput}
                                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, soleTraderInput: e.target.value})}
                                />
                            </div>
                        </div>
                    </fieldset>
                    {errors.company && <div className="invalid-feedback">{errors.company}</div>}
                </div>

            <div className="col-md-6">
                <label htmlFor="credit-rating" className="form-label">How is your credit rating?&nbsp;
                    <span aria-label="required">*</span>
                </label>
                <input 
                    type="range"
                    id="credit-rating"
                    name="credit-rating"
                    className="form-range lqc-form-range"
                    min="0"
                    max="2"
                    step="1" 
                    value={loanQuotationInput.creditRatingCode}
                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, creditRatingCode: e.target.value})}
                />
                <div className="d-flex justify-content-between px-1 text-muted small">
                    <span>Excellent</span>
                    <span>Average</span>
                    <span>Poor</span>
                </div>
            </div>

            <div className="col-md-6">
                <label htmlFor="contact-name" className="form-label">Contact name&nbsp;
                    <span aria-label="required">*</span>
                </label>
                <input
                    type="text"
                    id="contact-name"
                    name="contact-name"
                    className={`form-control ${errors.contactNameInput ? "is-invalid" : ""}`}
                    value={loanQuotationInput.contactNameInput}
                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, contactNameInput: e.target.value})}
                />
                {errors.contactNameInput && <div className="invalid-feedback">{errors.contactNameInput}</div>}
            </div>
            <div className="col-md-6">
                <label htmlFor="email" className="form-label">Email&nbsp;
                    <span aria-label="required">*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${errors.emailInput ? "is-invalid" : ""}`}
                    value={loanQuotationInput.emailInput}
                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, emailInput: e.target.value})}
                />
                {errors.emailInput && <div className="invalid-feedback">{errors.emailInput}</div>}
            </div>
            <div className="col-md-6">
                <label htmlFor="phone" className="form-label">Phone&nbsp;
                    <span aria-label="required">*</span>
                </label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className={`form-control ${errors.phoneInput ? "is-invalid" : ""}`}
                    value={loanQuotationInput.phoneInput}
                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, phoneInput: e.target.value})}
                />
                {errors.phoneInput && <div className="invalid-feedback">{errors.phoneInput}</div>}
            </div>
            <div className="col-md-12">
                <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                    {isSubmitting ? "Getting quote..." : "Get a quote!"}
                </button>
            </div>
            </div>
        </form>
    );
}

export default LoanQuoteStep1;
